import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, ArrowRight, FileText, CheckCircle2, Copy } from 'lucide-react';
import BankTransferDetails from '../components/common/BankTransferDetails';

export function TaxDonationPage() {
  const [copied, setCopied] = React.useState(false);

  const copyKrsToClipboard = async () => {
    try {
      await navigator.clipboard.writeText('0000437387');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback na wypadek braku uprawnień do schowka
      const textarea = document.createElement('textarea');
      textarea.value = '0000437387';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <Helmet>
        <title>Przekaż 1.5% podatku | Fundacja "Znajdki"</title>
        <meta name="description" content="Przekaż 1.5% swojego podatku na pomoc bezdomnym zwierzętom. Rozlicz PIT z PITAX i wesprzyj Fundację Znajdki." />
      </Helmet>

      <div className="text-center mb-12">
        <h1 className="heading-1 mb-6">
          Przekaż 1.5% podatku
        </h1>
        <div className="relative w-full max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="/images/animals/fundacja-znajdki-img-5968.webp"
            alt="Psy i koty w schronisku – Fundacja Znajdki"
            className="w-full h-64 sm:h-72 md:h-80 lg:h-[500px] xl:h-[600px] object-cover object-center object-[0%_25%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="heading-4-white font-medium max-w-2xl mx-auto no-margin">
              Twój 1.5% podatku to dla nich szansa na lepsze życie
            </p>
          </div>
        </div>
        <div className="body-large max-w-3xl mx-auto space-y-4 text-left">
          <p>
            Te 1,5% to dla nas coś więcej niż cyfry - to finansowa poduszka bezpieczeństwa, która pozwala nam reagować natychmiast, gdy liczą się sekundy.
          </p>
          <p>
            Dzięki Tobie nie musimy odmawiać pomocy, gdy odbieramy telefon o potrąconym psie czy skrajnie wycieńczonym kocie. Te środki to natychmiastowa operacja, zapas ratujących życie leków i pewność, że gdy nadejdzie najgorsze, będziemy mieli za co walczyć o każde uderzenie serca naszych podopiecznych.
          </p>
          <p>
            Twoje wsparcie to nasz 'fundusz życia' na nagłe interwencje, których nie da się przewidzieć, a których nie wolno zignorować.
          </p>
        </div>
      </div>

      {/* W prostym języku */}
      <section className="mb-8">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-2">Możesz rozliczyć PIT online przez PITAX (na nasz KRS 0000437387) lub przepisać dane ręcznie w swoim formularzu.</p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
        {/* Quick Tax Filing */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-accessible-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
              Rozlicz PIT online
            </h2>
          </div>
          
          <div className="mb-6">
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="/images/animals/fundacja-znajdki-zwierzeta-018.webp"
                alt="Kot w schronisku – Fundacja Znajdki"
                className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          </div>
          
          <p className="body-medium mb-6">
            Wypełnij swój e-PIT za pomocą PITAXA! To bezpieczny i zautoryzowany program online do rozliczania podatków, współpracujący z e-Deklaracjami. Dzięki pośrednictwu, dane naszej Fundacji będą już w odpowiednich rubrykach w składanej przez Ciebie deklaracji podatkowej!
          </p>

          <a 
            href="https://www.pitax.pl/rozliczenie-pit-online-0000437387/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors btn-text-medium"
          >
            <span>Rozlicz PIT teraz</span>
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Manual Filing Instructions */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-accessible-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
              Dane do deklaracji
            </h2>
          </div>
          
          <div className="mb-6">
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="/images/animals/fundacja-znajdki-zwierzeta-019.webp"
                alt="Pies w schronisku – Fundacja Znajdki"
                className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-gray-200 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded-lg">
              <h3 className="heading-5 text-accessible-gray-900 dark:text-white high-contrast:text-black mb-2">
                Numer KRS
              </h3>
              <div className="flex items-center justify-between">
                <p className="heading-4 font-mono font-bold text-accessible-gray-900 dark:text-white tracking-wider no-margin">
                  0000437387
                </p>
                <button
                  type="button"
                  onClick={copyKrsToClipboard}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj numer KRS"
                  aria-label="Skopiuj numer KRS"
                >
                  {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" aria-hidden="true" />}
                </button>
              </div>
            </div>

            <div>
              <h3 className="heading-4 mb-4">
                Instrukcja wypełnienia:
              </h3>
              <p className="body-large mb-6">
                Jeśli wypełniasz PIT ręcznie, to żeby przekazać 1.5% podatku na pomoc ZNAJDKI, wpisz w odpowiednich rubrykach poniższe informacje, zgodnie z naszą krótką instrukcją:
              </p>
              <ol className="space-y-6 body-large">
                <li className="flex gap-2">
                  <span className="font-semibold">1.</span>
                  <span>Znajdź sekcję "WNIOSEK O PRZEKAZANIE 1.5% PODATKU NALEŻNEGO NA RZECZ ORGANIZACJI POŻYTKU PUBLICZNEGO (OPP)"</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold">2.</span>
                  <span>Wpisz numer KRS: 0000437387</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold">3.</span>
                  <span>W polu "Wnioskowana kwota" wpisz kwotę 1.5% należnego podatku</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold">4.</span>
                  <span>Możesz zaznaczyć pole "Wyrażam zgodę" jeśli chcesz, abyśmy wiedzieli od kogo otrzymaliśmy wsparcie</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold">5.</span>
                  <span>Ostatnie pole tej części możesz zostawić puste</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Transfer Details */}
      <BankTransferDetails className="mb-16" />

      {/* How We Use the Funds */}
      <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg mb-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-accessible-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
            Na co przeznaczymy Twój 1.5%?
          </h2>
        </div>
      
        {/* Responsive image grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          <div className="relative h-64 rounded-2xl overflow-hidden">
            <img
              src="/images/animals/fundacja-znajdki-zwierzeta-020.webp"
              alt="Weterynarz badający psa – Fundacja Znajdki"
              className="w-full h-full object-cover object-[0%_80%] hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
          <div className="relative h-64 rounded-2xl overflow-hidden">
            <img
              src="/images/animals/fundacja-znajdki-zwierzeta-021.webp"
              alt="Kot w schronisku – Fundacja Znajdki"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
          <div className="relative h-64 rounded-2xl overflow-hidden md:col-span-2 lg:col-span-1">
            <img
              src="/images/animals/fundacja-znajdki-zwierzeta-022.webp"
              alt="Szczeniak w schronisku – Fundacja Znajdki"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            'Leczenie i rehabilitację najbardziej potrzebujących zwierzaków',
            'Kastrację i sterylizację znajdkowych psów i kotów',
            'Środki higieny dla niepełnosprawnych podopiecznych',
            'Pożywną karmę i suplementy dla znajdek'
          ].map((item, index) => (
            <div
              key={index}
              className="p-6 bg-gray-200 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded-lg flex items-start gap-3 hover:transform hover:scale-105 transition-transform duration-300"
            >
              <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
              <p className="body-medium text-accessible-gray-600 dark:text-accessible-gray-300">
                {item}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}